import React, { useState, useEffect } from 'react';
import { Add, Edit, DeleteOutline } from '@mui/icons-material';
import { holidayService } from '../services/api';
import { PillButton } from './ui/Buttons';

interface Holiday {
  id: number;
  name: string;
  date: string;
  description: string;
  isActive: boolean;
  createdAt: string;
}

const HolidayManagement: React.FC = () => {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const data = await holidayService.getHolidays();
      setHolidays(data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingHoliday) {
        await holidayService.updateHoliday(editingHoliday.id, formData);
      } else {
        await holidayService.createHoliday(formData);
      }
      setShowForm(false);
      setEditingHoliday(null);
      setFormData({ name: '', date: '', description: '' });
      fetchHolidays();
    } catch (error) {
      console.error('Error saving holiday:', error);
      alert('Error saving holiday');
    }
  };

  const handleEdit = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date.split('T')[0],
      description: holiday.description
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      try {
        await holidayService.deleteHoliday(id);
        fetchHolidays();
      } catch (error) {
        console.error('Error deleting holiday:', error);
        alert('Error deleting holiday');
      }
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingHoliday(null);
    setFormData({ name: '', date: '', description: '' });
  };

  if (loading) {
    return <div>Loading holidays...</div>;
  }

  return (
    <div className="holiday-management">
      <div className="header">
        <h2>Holiday Management</h2>
        <PillButton variant="success" onClick={() => setShowForm(true)}>
          <Add sx={{ fontSize: 16 }} /> Add Holiday
        </PillButton>
      </div>

      <div className="holidays-grid">
        {holidays.map(holiday => (
          <div key={holiday.id} className="holiday-card">
            <div className="holiday-header">
              <h3>{holiday.name}</h3>
              <span className="holiday-date">
                {new Date(holiday.date).toLocaleDateString()}
              </span>
            </div>
            <p className="holiday-description">{holiday.description}</p>
            <div className="holiday-actions">
              <PillButton variant="warning" size="sm" onClick={() => handleEdit(holiday)}>
                <Edit sx={{ fontSize: 14 }} /> Edit
              </PillButton>
              <PillButton variant="danger" size="sm" onClick={() => handleDelete(holiday.id)}>
                <DeleteOutline sx={{ fontSize: 14 }} /> Delete
              </PillButton>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h3>{editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Holiday Name:</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Date:</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <PillButton type="submit" variant="success">
                  {editingHoliday ? 'Update' : 'Create'}
                </PillButton>
                <PillButton type="button" variant="neutral" onClick={resetForm}>
                  Cancel
                </PillButton>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .holiday-management {
          padding: 20px;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }
        .holidays-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }
        .holiday-card {
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 20px;
          background: white;
          box-shadow: 0 2px 4px rgba(15,23,42,0.08);
        }
        .holiday-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .holiday-header h3 {
          margin: 0;
          color: #1e293b;
        }
        .holiday-date {
          background: #7c3aed;
          color: white;
          padding: 4px 8px;
          border-radius: 10px;
          font-size: 0.9em;
        }
        .holiday-description {
          color: #64748b;
          margin-bottom: 15px;
        }
        .holiday-actions {
          display: flex;
          gap: 10px;
        }
        .modal {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(2,6,23,0.6);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal-content {
          background: white;
          padding: 20px;
          border-radius: 16px;
          width: 400px;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: bold;
        }
        .form-group input, .form-group textarea {
          width: 100%;
          padding: 8px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
        }
        .form-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }
      `}</style>
    </div>
  );
};

export default HolidayManagement;